/* AdminComplianceDuplicatiPage — Dashboard tentativi duplicato CF/PIVA bloccati.
 * FASE 4.2.10 TASK D. Aggrega audit_logs.action="duplicate_blocked".
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Shield, AlertTriangle, Download, Calendar } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIE_COLORS = ["#163321", "#D97B58"];

export default function AdminComplianceDuplicatiPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodoDa, setPeriodoDa] = useState("");
  const [periodoA, setPeriodoA] = useState("");

  const adminToken = () => {
    try { return JSON.parse(localStorage.getItem("colucci_admin_session") || "{}").token || ""; }
    catch (_e) { return ""; }
  };

  const fetchData = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (periodoDa) params.append("periodo_da", periodoDa);
    if (periodoA) params.append("periodo_a", periodoA);
    axios.get(`${API}/admin/crm/multi/compliance/duplicati-bloccati?${params.toString()}`, {
      headers: { Authorization: `Bearer ${adminToken()}` },
    })
      .then((r) => setData(r.data))
      .catch((err) => console.error("compliance fetch error", err))
      .finally(() => setLoading(false));
  };
  useEffect(fetchData, [periodoDa, periodoA]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Data", "Count"]];
    (data.per_giorno || []).forEach((d) => rows.push([d.data, d.count]));
    rows.push([""]);
    rows.push(["CF masked", "Tentativi", "Primo", "Ultimo"]);
    (data.top_cf_ripetuti || []).forEach((r) => rows.push([r.masked, r.tentativi, r.first_blocked, r.last_blocked]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `duplicati-bloccati-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pieData = data ? [
    { name: "CF", value: data.per_tipo?.CF || 0 },
    { name: "PIVA", value: data.per_tipo?.PIVA || 0 },
  ] : [];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-compliance-page">
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-2">
            CRM Multi-Compagnia · Compliance
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#163321] flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#D97B58]" /> Duplicati KYC bloccati
          </h1>
          <p className="text-sm text-[#4A5E51] mt-2 max-w-2xl">
            Tentativi di inserimento clienti con CF/PIVA duplicato bloccati dall'integrità
            anagrafica (audit IVASS).
          </p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#163321] text-white text-sm font-semibold hover:bg-[#1F4030]" data-testid="export-csv-btn">
          <Download className="w-4 h-4" /> Esporta CSV
        </button>
      </header>

      {/* Filtri date */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#4A5E51]" />
          <input type="date" value={periodoDa} onChange={(e) => setPeriodoDa(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="filter-da" />
          <span className="text-xs text-[#4A5E51]">→</span>
          <input type="date" value={periodoA} onChange={(e) => setPeriodoA(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="filter-a" />
        </div>
      </div>

      {loading ? <p className="text-sm text-[#4A5E51]">Caricamento…</p> : data && (
        <div className="space-y-6">
          {/* KPI grande */}
          <div className="bg-gradient-to-br from-[#163321] to-[#264A38] rounded-2xl p-8 text-white" data-testid="kpi-totale">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-[#D97B58]" />
              <p className="text-xs uppercase tracking-widest opacity-80">Tentativi bloccati</p>
            </div>
            <p className="font-heading text-6xl font-bold">{data.totale_tentativi_bloccati}</p>
            <p className="text-sm opacity-70 mt-2">
              {data.per_tipo?.CF || 0} duplicati CF · {data.per_tipo?.PIVA || 0} duplicati PIVA
            </p>
          </div>

          {/* Grafici */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="chart-per-giorno">
              <h3 className="font-heading font-bold text-[#163321] mb-4">Tentativi per giorno</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.per_giorno}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DB" />
                  <XAxis dataKey="data" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#D97B58" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="chart-per-tipo">
              <h3 className="font-heading font-bold text-[#163321] mb-4">CF vs PIVA</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {pieData.map((_e, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabella top CF ripetuti */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="top-cf-table">
            <h3 className="font-heading font-bold text-[#163321] p-5 pb-3">Top CF/PIVA ripetuti</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[#4A5E51] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3 text-left">Identificativo</th>
                    <th className="px-5 py-3 text-left">Tipo</th>
                    <th className="px-5 py-3 text-right">Tentativi</th>
                    <th className="px-5 py-3 text-left">Primo</th>
                    <th className="px-5 py-3 text-left">Ultimo</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.top_cf_ripetuti || []).length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-[#4A5E51]">Nessun duplicato bloccato nel periodo selezionato.</td></tr>
                  )}
                  {(data.top_cf_ripetuti || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-5 py-3 font-mono">{r.masked}</td>
                      <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full bg-[#163321]/10 text-[#163321] text-xs font-semibold">{r.tipo}</span></td>
                      <td className="px-5 py-3 text-right font-semibold">{r.tentativi}</td>
                      <td className="px-5 py-3 text-[#4A5E51]">{r.first_blocked}</td>
                      <td className="px-5 py-3 text-[#4A5E51]">{r.last_blocked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per admin */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <h3 className="font-heading font-bold text-[#163321] p-5 pb-3">Per utente admin</h3>
            <ul className="px-5 pb-5">
              {(data.per_utente_admin || []).map((u, i) => (
                <li key={i} className="flex items-center justify-between py-2 border-t border-slate-100 text-sm">
                  <span className="text-[#163321]">{u.user_email}</span>
                  <span className="font-semibold">{u.tentativi}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
